import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic, MicOff, X, Send, Loader2, Sparkles, Plane, Building2,
  MapPin, Calendar, Users, ArrowRight, Volume2, VolumeX,
  Compass, Info, ChevronRight, Bot, User, Globe, Star
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Speech Recognition setup
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
const hasSpeechRecognition = !!SpeechRecognition;

export default function TravelConcierge({ 
  onFlightSearch, 
  onHotelSearch, 
  onStaySearch,
  currentPage = 'travel' 
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [sessionId] = useState(() => `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize speech recognition
  useEffect(() => {
    if (hasSpeechRecognition) {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        setInputText(transcript);
        
        if (event.results[0].isFinal) {
          setIsListening(false);
          // Auto-send after voice input
          setTimeout(() => handleSendMessage(transcript), 500);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error !== 'aborted') {
          toast.error('Voice recognition error. Please try again.');
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
    };
  }, []);

  // Fetch greeting when panel opens
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      fetchGreeting();
    }
  }, [isOpen]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchGreeting = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/ai-concierge/greeting`);
      if (response.data.success) {
        setMessages([{
          id: Date.now(),
          role: 'assistant',
          content: response.data.greeting,
          type: 'greeting'
        }]);
      }
    } catch (error) {
      console.error('Failed to fetch greeting:', error);
      setMessages([{
        id: Date.now(),
        role: 'assistant',
        content: "Hello! I'm your AIRA Travel Concierge. How may I assist you with your travel plans today?",
        type: 'greeting'
      }]);
    }
  };

  const toggleListening = () => {
    if (!hasSpeechRecognition) {
      toast.error('Voice recognition is not supported in your browser. Please use Chrome or Edge.');
      return;
    }

    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      setInputText('');
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  const handleSendMessage = useCallback(async (text = inputText) => {
    if (!text.trim() || isProcessing) return;

    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: text.trim()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputText('');
    setIsProcessing(true);

    try {
      // Use the new AI Concierge API with Jev integration
      const response = await axios.post(`${API_URL}/api/ai-concierge/query`, {
        query: text.trim(),
        session_id: sessionId,
        conversation_context: messages.slice(-5).map(m => m.content)
      });

      if (response.data.success) {
        const data = response.data;
        
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.response || 'I understand your request.',
          type: data.service,
          action: data.action,
          data: data
        };

        setMessages(prev => [...prev, assistantMessage]);

        // Handle different actions based on Jev routing
        if (data.action === 'confirm' && data.parameters) {
          // Parameters extracted, ready for user confirmation
          if (data.service === 'flights') {
            handleFlightRequest(data);
          } else if (data.service === 'hotels') {
            handleHotelRequest(data);
          } else if (data.service === 'cabs') {
            handleCabRequest(data);
          } else if (data.service === 'insurance') {
            handleInsuranceRequest(data);
          } else if (data.service === 'tours') {
            handleToursRequest(data);
          }
        } else if (data.action === 'clarify') {
          // Need more information - message already shown
          toast.info(`Please provide: ${data.missing_info?.join(', ') || 'more details'}`);
        }
        // action === 'chat' or 'error' - just show the response
      }
    } catch (error) {
      console.error('AI Concierge error:', error);
      setMessages(prev => [...prev, {
        id: Date.now() + 1,
        role: 'assistant',
        content: 'I apologize, but I encountered an issue processing your request. Please try again or use the manual search.',
        type: 'error'
      }]);
    } finally {
      setIsProcessing(false);
    }
  }, [inputText, isProcessing, sessionId, messages, onFlightSearch, onHotelSearch, onStaySearch, setIsOpen]);

  const handleFlightRequest = useCallback((data) => {
    const params = data.parameters || data;
    
    if (data.missing_info && data.missing_info.length > 0) {
      toast.info(`I need a few more details: ${data.missing_info.join(', ')}`);
      return;
    }

    const searchParams = {
      origin: params.origin_code || params.origin || '',
      destination: params.destination_code || params.destination || '',
      departure_date: params.departure_date || '',
      return_date: params.return_date || '',
      adults: params.adults || 1,
      children: params.children || 0,
      infants: params.infants || 0,
      travel_class: params.travel_class || 'ECONOMY',
      trip_type: params.trip_type || 'oneway'
    };

    if (onFlightSearch) {
      onFlightSearch(searchParams);
      toast.success('Flight search form filled! Review and search.');
      // Don't auto-close - let user confirm
    }
  }, [onFlightSearch]);

  const handleHotelRequest = useCallback((data) => {
    const params = data.parameters || data;
    
    if (data.missing_info && data.missing_info.length > 0) {
      toast.info(`I need a few more details: ${data.missing_info.join(', ')}`);
      return;
    }

    const searchParams = {
      destination: params.destination_code || params.destination || '',
      check_in: params.check_in || '',
      check_out: params.check_out || '',
      guests: params.guests || 2,
      rooms: params.rooms || 1,
      preferences: params.preferences || []
    };

    if (onHotelSearch) {
      onHotelSearch(searchParams);
      toast.success('Hotel search form filled! Review and search.');
    } else if (onStaySearch) {
      onStaySearch(searchParams);
      toast.success('Stay search form filled! Review and search.');
    }
  }, [onHotelSearch, onStaySearch]);

  const handleCabRequest = useCallback((data) => {
    const params = data.parameters || data;
    
    if (data.missing_info && data.missing_info.length > 0) {
      toast.info(`I need a few more details: ${data.missing_info.join(', ')}`);
      return;
    }

    // For cabs, we show a toast with the extracted info
    // The actual cab booking flow will use these parameters
    toast.success(`Cab request: ${params.pickup_location} → ${params.drop_location}`, {
      description: `Pickup: ${params.pickup_datetime || 'Not specified'}`,
      action: {
        label: 'Book Cab',
        onClick: () => {
          // Navigate to cabs section or trigger cab search
          window.location.href = '/travel?tab=cabs';
        }
      }
    });
  }, []);

  const handleInsuranceRequest = useCallback((data) => {
    const params = data.parameters || data;
    
    if (data.missing_info && data.missing_info.length > 0) {
      toast.info(`I need a few more details: ${data.missing_info.join(', ')}`);
      return;
    }

    // Show insurance info toast
    toast.success(`Insurance request: Travel to ${params.destination_region || 'your destination'}`, {
      description: `${params.travel_start_date || ''} - ${params.travel_end_date || ''}`,
      action: {
        label: 'Get Insurance',
        onClick: () => {
          window.location.href = '/travel?tab=insurance';
        }
      }
    });
  }, []);

  const handleToursRequest = useCallback((data) => {
    const params = data.parameters || data;
    
    // Show tours info toast
    toast.success(`Tours in ${params.destination || 'your destination'}`, {
      description: params.activity_type || 'Explore experiences',
      action: {
        label: 'View Tours',
        onClick: () => {
          window.location.href = '/travel?tab=tours';
        }
      }
    });
  }, []);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Quick action buttons
  const quickActions = [
    { icon: Plane, label: 'Book a Flight', prompt: 'Help me book a flight' },
    { icon: Building2, label: 'Find Hotels', prompt: 'Find me a hotel' },
    { icon: Compass, label: 'Discover Places', prompt: 'Recommend travel destinations' },
    { icon: Star, label: 'Luxury Stays', prompt: 'Show me luxury accommodations' },
  ];

  return (
    <>
      {/* Floating Concierge Button */}
      <motion.button
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-16 h-16 rounded-full bg-gradient-to-r from-[#FF007F] via-[#7B2FBE] to-[#00E5FF] shadow-[0_0_40px_rgba(255,0,127,0.5)] flex items-center justify-center group"
        data-testid="travel-concierge-btn"
      >
        <div className="absolute inset-0.5 rounded-full bg-[#12101A] flex items-center justify-center">
          <Bot className="w-7 h-7 text-white group-hover:text-[#00E5FF] transition-colors" />
        </div>
        {/* Pulse animation */}
        <div className="absolute inset-0 rounded-full bg-gradient-to-r from-[#FF007F] to-[#00E5FF] animate-ping opacity-20" />
      </motion.button>

      {/* Concierge Panel */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />

            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed right-0 top-0 h-full w-full max-w-md bg-[#0D0B14] border-l border-white/10 z-50 flex flex-col overflow-hidden"
              data-testid="travel-concierge-panel"
            >
              {/* Header */}
              <div className="relative p-6 border-b border-white/10 bg-gradient-to-b from-[#1a1625] to-transparent">
                <button
                  onClick={() => setIsOpen(false)}
                  className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <X className="w-5 h-5 text-white/70" />
                </button>
                
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#FF007F] to-[#00E5FF] p-0.5">
                    <div className="w-full h-full rounded-2xl bg-[#12101A] flex items-center justify-center">
                      <Sparkles className="w-7 h-7 text-[#00E5FF]" />
                    </div>
                  </div>
                  <div>
                    <h2 className="font-['Playfair_Display'] text-xl text-white font-bold">AIRA Travel Concierge</h2>
                    <p className="text-white/50 text-sm">Your premium travel assistant</p>
                  </div>
                </div>

                {/* Status indicator */}
                <div className="mt-4 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-emerald-400 text-xs uppercase tracking-wider">Available 24/7</span>
                </div>
              </div>

              {/* Messages Area */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-hide">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl p-4 ${
                        message.role === 'user'
                          ? 'bg-gradient-to-r from-[#FF007F] to-[#7B2FBE] text-white'
                          : 'bg-white/5 border border-white/10 text-white/90'
                      }`}
                    >
                      {message.role === 'assistant' && (
                        <div className="flex items-center gap-2 mb-2">
                          <Sparkles className="w-4 h-4 text-[#00E5FF]" />
                          <span className="text-[#00E5FF] text-xs uppercase tracking-wider">Concierge</span>
                        </div>
                      )}
                      <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>
                      
                      {/* Recommendations */}
                      {message.data?.recommendations && (
                        <div className="mt-4 space-y-3">
                          {message.data.recommendations.map((rec, idx) => (
                            <div key={idx} className="bg-white/5 rounded-xl p-3 border border-white/10">
                              <h4 className="font-semibold text-white flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-[#FF007F]" />
                                {rec.destination}
                              </h4>
                              <p className="text-white/60 text-xs mt-1">{rec.description}</p>
                              {rec.best_time && (
                                <p className="text-[#00E5FF] text-xs mt-1">Best time: {rec.best_time}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Action buttons for flight/hotel results */}
                      {message.data?.type === 'flight' && message.data?.confidence >= 0.7 && (
                        <div className="mt-3 p-3 bg-black/20 rounded-xl">
                          <div className="flex items-center gap-2 text-xs text-white/60 mb-2">
                            <Plane className="w-4 h-4" />
                            <span>Flight Search Ready</span>
                          </div>
                          <div className="text-sm">
                            <p><span className="text-white/50">From:</span> {message.data.origin}</p>
                            <p><span className="text-white/50">To:</span> {message.data.destination}</p>
                            <p><span className="text-white/50">Date:</span> {message.data.departure_date}</p>
                          </div>
                        </div>
                      )}

                      {(message.data?.type === 'hotel' || message.data?.type === 'stay') && message.data?.confidence >= 0.7 && (
                        <div className="mt-3 p-3 bg-black/20 rounded-xl">
                          <div className="flex items-center gap-2 text-xs text-white/60 mb-2">
                            <Building2 className="w-4 h-4" />
                            <span>Hotel Search Ready</span>
                          </div>
                          <div className="text-sm">
                            <p><span className="text-white/50">City:</span> {message.data.destination}</p>
                            <p><span className="text-white/50">Check-in:</span> {message.data.check_in}</p>
                            <p><span className="text-white/50">Check-out:</span> {message.data.check_out}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </motion.div>
                ))}

                {/* Processing indicator */}
                {isProcessing && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex justify-start"
                  >
                    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                      <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" />
                        <span className="text-white/60 text-sm">Processing your request...</span>
                      </div>
                    </div>
                  </motion.div>
                )}

                <div ref={messagesEndRef} />
              </div>

              {/* Quick Actions */}
              {messages.length <= 1 && (
                <div className="px-4 py-3 border-t border-white/5">
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-3">Quick Actions</p>
                  <div className="grid grid-cols-2 gap-2">
                    {quickActions.map((action, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setInputText(action.prompt);
                          handleSendMessage(action.prompt);
                        }}
                        className="flex items-center gap-2 p-3 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all text-left group"
                      >
                        <action.icon className="w-4 h-4 text-[#FF007F] group-hover:text-[#00E5FF] transition-colors" />
                        <span className="text-white/70 text-sm group-hover:text-white transition-colors">{action.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Input Area */}
              <div className="p-4 border-t border-white/10 bg-gradient-to-t from-[#0D0B14] to-transparent">
                {/* Voice indicator */}
                <AnimatePresence>
                  {isListening && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="mb-3 p-3 rounded-xl bg-[#FF007F]/10 border border-[#FF007F]/30"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full bg-[#FF007F] animate-pulse" />
                        <span className="text-[#FF007F] text-sm">Listening... Speak your travel request</span>
                      </div>
                      {/* Voice waveform animation */}
                      <div className="flex items-center justify-center gap-1 mt-2">
                        {[...Array(12)].map((_, i) => (
                          <motion.div
                            key={i}
                            animate={{
                              height: [4, Math.random() * 24 + 4, 4],
                            }}
                            transition={{
                              duration: 0.5,
                              repeat: Infinity,
                              delay: i * 0.05,
                            }}
                            className="w-1 bg-[#FF007F] rounded-full"
                          />
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div className="flex items-center gap-2">
                  {/* Voice button */}
                  <button
                    onClick={toggleListening}
                    disabled={isProcessing}
                    className={`p-3 rounded-xl transition-all ${
                      isListening
                        ? 'bg-[#FF007F] text-white shadow-[0_0_20px_rgba(255,0,127,0.5)]'
                        : 'bg-white/5 text-white/70 hover:bg-white/10 hover:text-white'
                    } disabled:opacity-50`}
                    data-testid="voice-btn"
                  >
                    {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
                  </button>

                  {/* Text input */}
                  <div className="flex-1 relative">
                    <input
                      ref={inputRef}
                      type="text"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Type or speak your travel request..."
                      disabled={isProcessing || isListening}
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/30 focus:border-[#00E5FF] focus:ring-1 focus:ring-[#00E5FF] transition-all disabled:opacity-50"
                      data-testid="concierge-input"
                    />
                  </div>

                  {/* Send button */}
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputText.trim() || isProcessing}
                    className="p-3 rounded-xl bg-gradient-to-r from-[#00E5FF] to-[#00B8D4] text-black hover:shadow-[0_0_20px_rgba(0,229,255,0.4)] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="send-btn"
                  >
                    {isProcessing ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <Send className="w-5 h-5" />
                    )}
                  </button>
                </div>

                {/* Voice hint */}
                {hasSpeechRecognition && (
                  <p className="text-center text-white/30 text-xs mt-3">
                    Tap the microphone to speak your request
                  </p>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
